import { Tooltip, TooltipTrigger } from "@artis/ui";
import { FaTrash } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";

import { useActivePageId, usePageProduct, usePageProducts, useProductById } from "../../../../data";
import { cellInside, rangeLabel, SelectionInfo } from "../SelectionInfo";
import type { TRangeSelection } from "../../useRangeSelectionChange";
import { HelperInfoIcon, PointerIcon } from "../../../icons";
import { useGrid } from "../../stores";
import { targetCells } from "../util";
import { Button, Checkbox, Toggle } from "../../../ui";

import { ManageTable } from "../Shared";
import { Dropdown } from "../../../ui/dropdown";
import { AlertRow } from "./AlertRow";
import { isNullish } from "remeda";
import { useOnSave } from "../ModalContainer";
import { formatManageAlertRuleTooltip } from "../../../alerts/priceAlerts";
import { useDeleteAlerts, useQueryAlerts, useUpsertAlerts } from "../../../../data/alerts";
import type { TAlert } from "../../../../data/alerts/queries";
import { rowIdToPeriod } from "../../../../data/formatting/helpers";
import {
  calculateExpiredAt,
  isAlertFullyDisabled,
  isAlertPeriodExpired,
  isAutoExpiredAt,
  isExpiredAt,
  resolveExpiredAt,
} from "../../../../data/alerts/expiration";

// Sort order requested in WEB-1436: Active > Inactive > Expired, then newest first.
const statusRank = (alert: TAlert, now: Date) => {
  if (isExpiredAt(resolveExpiredAt(alert), now)) return 2;
  if (isAlertPeriodExpired(alert, now) || alert.status !== "Active") return 1;
  return 0;
};

const sortAlerts = <T extends TAlert>(alerts: T[]): T[] => {
  const now = new Date();
  return [...alerts].sort((a, b) => {
    const rank = statusRank(a, now) - statusRank(b, now);
    if (rank !== 0) return rank;
    return (b.created_at ?? "").localeCompare(a.created_at ?? "");
  });
};

const FILTER_OPTIONS = [
  { value: "range", label: "Range" },
  { value: "page", label: "Page" },
  { value: "all", label: "All pages" },
] as const;
type AlertsFilter = (typeof FILTER_OPTIONS)[number]["value"];

export const ManageAlerts = ({ selection }: { selection?: TRangeSelection }) => {
  const hasRangeSelection = selection?.some((row) => row.length > 0) ?? false;
  const [filter, setFilter] = useState<AlertsFilter>(hasRangeSelection ? "range" : "page");
  const [showExpired, setShowExpired] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [deleted, setDeleted] = useState<string[]>([]);
  const pageId = useActivePageId();
  const [gridApi] = useGrid();

  const remove = useDeleteAlerts();
  const upsert = useUpsertAlerts();
  const alerts = useQueryAlerts();

  const [values, setValues] = useState((alerts.data ?? []).map((x) => ({ ...x, changed: false })));

  useEffect(() => {
    // Rows now refresh while the modal is open, so keep edits that are still waiting to be saved.
    setValues((prev) =>
      (alerts.data ?? []).map((x) => {
        const edited = prev.find((y) => y.id === x.id && y.changed);
        return edited ?? { ...x, changed: false };
      }),
    );
  }, [alerts.data]);

  // An alert's stored `column_id` is really just the grid column *instance* it was created
  // against (`page_products.grid_id`). It's not stable: removing a column from the page (or
  // removing then re-adding the same curve) deletes/replaces that `page_products` row, so the
  // alert's `column_id` goes stale even though the alert itself is never deleted and keeps
  // triggering fine (evaluation keys off the stable `product_id`, not `column_id` - see
  // `getActiveAlerts`). Only grid-position features here (range filter, highlight) need a
  // *live* column id, so resolve it from the current page products by `product_id`, falling
  // back to the possibly-stale stored one (e.g. if the curve was removed from the page
  // entirely, in which case there's no live column to find either way).
  const pageProducts = usePageProducts(pageId);
  const currentColumnIdByProductId = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of pageProducts.data ?? []) {
      if (p.product_id) map.set(p.product_id, p.grid_id || p.id);
    }
    return map;
  }, [pageProducts.data]);
  const resolveColumnId = (alert: TAlert) => currentColumnIdByProductId.get(alert.product_id) ?? alert.column_id;

  const now = new Date();
  // Scoped by the range/page/all filter, but not yet by "Show expired alerts".
  const inScope = values.filter((alert) => {
    if (deleted.includes(alert.id)) return false;
    if (filter === "all") return true;
    if (filter === "page") return alert.page_id === pageId;
    return cellInside(selection ?? [], resolveColumnId(alert), rowIdToPeriod(alert.row_id) || alert.row_id);
  });
  const visible = sortAlerts(
    showExpired ? inScope : inScope.filter((alert) => !isExpiredAt(resolveExpiredAt(alert), now)),
  );

  const onUpdate = (arg: Partial<TAlert> & { id: string }) => {
    setValues((prev) =>
      prev.map((x) => {
        if (x.id !== arg.id) return x;

        const updated = { ...x, ...arg };

        // Recalculate expired_at if limit changed, but only when the alert is still on the
        // "None" (auto/contract-derived) expiration — a user-picked fixed date must survive limit edits.
        if (arg.limit !== undefined && x.row_id && isAutoExpiredAt(x.expired_at, x.row_id, x.limit)) {
          updated.expired_at = calculateExpiredAt(x.row_id, arg.limit);
        }

        return { ...updated, changed: true };
      }),
    );
  };

  const onRemove = (id: string) => {
    setSelected((prev) => prev.filter((x) => x !== id));
    setDeleted((prev) => [...prev, id]);
  };

  useOnSave(async () => {
    const updates = values
      .filter((x) => x.changed && !deleted.includes(x.id))
      .map((x) => ({ ...x, changed: undefined }));

    for (const update of updates) {
      if (update.limit?.trim() === "" || isNullish(update.limit)) {
        throw new Error("All alerts must have a limit");
      }
    }

    await Promise.all([deleted.length && remove.mutateAsync(deleted), updates.length && upsert.mutateAsync(updates)]);
  });

  return (
    <>
      <SelectionInfo>{rangeLabel(selection ?? [])}</SelectionInfo>
      <div
        className="flex items-center justify-between mx-4"
        style={{
          marginTop: "var(--spacing-scale-small-400, 12px)",
          marginBottom: "var(--spacing-scale-small-400, 12px)",
        }}
      >
        <Dropdown
          value={filter}
          onChange={(value) => setFilter(value as AlertsFilter)}
          options={[...FILTER_OPTIONS]}
          className="w-[92px] h-[27px]"
        />
        <Toggle label="Show expired alerts" checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} />
      </div>

      <ManageTable className="[grid-template:auto/2.5rem_1px_auto_87.5px_auto_auto_5rem_auto_auto]">
        <ManageTable.Header>
          <div className="flex justify-center">
            <Checkbox
              aria-label="Select all alerts"
              checked={visible.length > 0 && selected.length === visible.length}
              onChange={(e) => setSelected(e.target.checked ? visible.map((x) => x.id) : [])}
            />
          </div>
          <div
            className="border-r border-outline h-[23px]"
            style={{ marginRight: "var(--spacing-scale-small-200, 6px)" }}
          />
          <div className="flex items-center">Rule</div>
          <div className="flex items-center">Limit</div>
          <div className="flex items-center">Note</div>
          <div className="flex items-center gap-[2px]">
            Expiration
            <TooltipTrigger placement="top" touch="toggle" focusable>
              <span className="group inline-flex size-[1rem] shrink-0 cursor-default items-center justify-center overflow-visible leading-none rounded-full transition-colors hover:bg-neutral-tonal">
                <HelperInfoIcon />
              </span>
              <Tooltip>Alerts will expire when the selected cell or reference cell's contract expires</Tooltip>
            </TooltipTrigger>
          </div>
          <div className="flex items-center">Status</div>
          <div className="flex items-center justify-center">Recurring</div>
          <div className="flex items-center justify-center">Sound</div>
        </ManageTable.Header>

        {visible.length === 0 && (
          <div className="col-span-full pt-2 pb-3 px-4 text-foreground-muted">
            No alerts in {FILTER_OPTIONS.find((o) => o.value === filter)?.label.toLowerCase()}
          </div>
        )}
        {visible.map((value) => (
          <ManageAlertRow
            key={value.id}
            value={value}
            pageId={pageId}
            selected={selected.includes(value.id)}
            onSelect={() =>
              setSelected((prev) =>
                prev.includes(value.id) ? prev.filter((x) => x !== value.id) : [...prev, value.id],
              )
            }
            onUpdate={(c) => onUpdate({ id: value.id, ...c })}
          />
        ))}
      </ManageTable>

      <ActionBar
        selected={selected}
        // Expired / contract-inactive alerts are read-only, so the whole selection is
        // blocked from highlighting if any of it is in that state.
        highlightDisabled={visible.some((x) => selected.includes(x.id) && isAlertFullyDisabled(x, now))}
        onDelete={onRemove}
        onTarget={() =>
          targetCells(
            gridApi,
            visible.filter((x) => selected.includes(x.id)).map((x) => ({ ...x, column_id: resolveColumnId(x) })),
          )
        }
      />
    </>
  );
};

const ManageAlertRow = ({
  value,
  pageId,
  selected,
  onSelect,
  onUpdate,
}: {
  value: TAlert;
  pageId: string;
  selected: boolean;
  onSelect: () => void;
  onUpdate: (rule: Partial<TAlert>) => void;
}) => {
  const pageProduct = usePageProduct(value.page_id, value.product_id);
  const product = useProductById(pageProduct.data?.product_id ?? value.product_id);
  const period = rowIdToPeriod(value.row_id) || value.row_id;
  const symbol = value.value_below_limit ? ">=" : "<=";
  const name = product.data?.name ?? "";

  return (
    <AlertRow
      value={value}
      pageId={pageId}
      selected={selected}
      affected={`${name} [${period}] ${symbol}`}
      affectedTooltip={formatManageAlertRuleTooltip(name, period, product.data?.packageByPackage?.name)}
      noteWidth={93}
      limitWidth={87.5} // InputOrReference subtracts 26px (icon button + gap), leaving a 61.5px input
      autoExpiredAt={calculateExpiredAt(value.row_id, value.limit)}
      onSelect={onSelect}
      onUpdate={onUpdate}
    />
  );
};

const ActionBar = ({
  onDelete,
  onTarget,
  selected,
  highlightDisabled,
}: {
  onDelete: (id: string) => void;
  onTarget?: () => void;
  selected: string[];
  highlightDisabled?: boolean;
}) => {
  const disabled = selected.length === 0;
  return (
    <div className="flex gap-4 my-3 px-3 flex-wrap items-center">
      <div className="flex-1 min-w-0" />
      <TooltipTrigger>
        <div>
          <Button
            variant="outline"
            color="neutral"
            size="iconOnly"
            iconWidth={36}
            onClick={onTarget}
            disabled={disabled || highlightDisabled}
          >
            <PointerIcon className="w-5 h-5" />
          </Button>
        </div>
        <Tooltip>
          {!disabled && highlightDisabled
            ? "Can't highlight an expired or inactive alert's cells"
            : "Highlight selected cells"}
        </Tooltip>
      </TooltipTrigger>
      <TooltipTrigger>
        <div>
          <Button
            variant="outline"
            color="neutral"
            size="iconOnly"
            iconWidth={36}
            aria-label="Delete the selected alert(s)"
            onClick={() => selected.map((x) => onDelete(x))}
            disabled={disabled}
          >
            <FaTrash />
          </Button>
        </div>
        <Tooltip>Delete the selected alert(s)</Tooltip>
      </TooltipTrigger>
    </div>
  );
};
